import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelector, useDispatch } from 'react-redux';
import CustomAxios from '../../utils/CustomAxios';
import { updatepageName } from '../../Redux/page';
import {
  GrowthModal,
  GrowthPrimaryButton,
  GrowthSecondaryButton,
  GrowthPageLoader,
  GrowthFormGrid,
} from '../growth_hub/GrowthModal';

const OTP_OPTIONS = [
  { value: 'SMS', label: 'SMS' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
];

const emptyForm = {
  inventory_ids: [],
  inventory: '',
  is_active: true,
  mpin_status: 'DISABLED',
  inactivity_threshold_days: '7',
  otp_channels: ['SMS'],
};

const REQ_TIMEOUT_MS = 20000;

function decodeOtpChannels(value) {
  const raw = String(value || 'SMS')
    .toUpperCase()
    .replace(/\s/g, '');
  if (raw === 'BOTH' || raw === 'SMS,WHATSAPP' || raw === 'WHATSAPP,SMS') {
    return ['SMS', 'WHATSAPP'];
  }
  const parts = raw.split(',').filter(Boolean);
  const allowed = new Set(['SMS', 'WHATSAPP']);
  const out = [];
  parts.forEach((p) => {
    if (allowed.has(p) && !out.includes(p)) out.push(p);
  });
  return out.length ? out : ['SMS'];
}

function encodeOtpChannels(channels) {
  const list = Array.isArray(channels) ? channels : [];
  const uniq = [];
  list.forEach((c) => {
    const v = String(c).toUpperCase();
    if ((v === 'SMS' || v === 'WHATSAPP') && !uniq.includes(v)) uniq.push(v);
  });
  if (!uniq.length) return 'SMS';
  if (uniq.length === 1) return uniq[0];
  return 'SMS,WHATSAPP';
}

function formatOtpChannelLabel(value) {
  const channels = decodeOtpChannels(value);
  if (channels.length === 2) return 'SMS + WhatsApp';
  return channels[0] === 'WHATSAPP' ? 'WhatsApp' : 'SMS';
}

/**
 * Hub-wise MPIN quick-unlock policy for mobile.
 */
export function MpinConfig() {
  const dispatch = useDispatch();
  const toast = useRef(null);
  const inventoryList = useSelector((state) => state.inventory.inventory) || [];

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const hubOptions = useMemo(
    () =>
      inventoryList.map((h) => ({
        value: Number(h.value ?? h.id),
        name: h.label || h.place || h.name || `Hub #${h.value ?? h.id}`,
      })),
    [inventoryList]
  );

  useEffect(() => {
    dispatch(updatepageName('mpin_config'));
    fetchConfigs({ showLoader: true });
  }, [dispatch]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3500 });
  };

  const hubLabel = (inventoryId) => {
    const hub = hubOptions.find((h) => Number(h.value) === Number(inventoryId));
    if (!hub) return `Hub #${inventoryId}`;
    return hub.name;
  };

  const fetchConfigs = async ({ showLoader = false } = {}) => {
    try {
      if (showLoader) setLoading(true);
      const res = await CustomAxios.get('mpin-config/', {
        timeout: REQ_TIMEOUT_MS,
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setConfigs(data);
    } catch (err) {
      showToast(
        'error',
        'Error',
        err?.code === 'ECONNABORTED'
          ? 'MPIN config load timed out — check API'
          : err?.response?.data?.detail || 'Failed to load MPIN configs'
      );
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const openCreate = () => {
    setEditMode(false);
    setSelectedId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  /**
   * Remove a hub's MPIN policy. The backend ViewSet is a ModelViewSet so DELETE
   * was already supported — the screen simply never offered it.
   *
   * Deleting the row does NOT delete anyone's PIN: resolve_hub_mpin_config
   * falls back to DISABLED for a hub with no row, so the hub stops enforcing
   * MPIN and existing UserMPIN records are left alone.
   */
  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleting(true);
      await CustomAxios.delete(`mpin-config/${deleteTarget.id}/`, {
        timeout: REQ_TIMEOUT_MS,
      });
      showToast('success', 'Deleted', 'MPIN config removed for this hub');
      setDeleteTarget(null);
      await fetchConfigs();
    } catch (err) {
      showToast(
        'error',
        'Delete failed',
        err?.response?.data?.detail || 'Could not delete this MPIN config'
      );
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelectedId(row.id);
    setForm({
      /*
       * A config now covers several hubs. Reading only `row.inventory` meant
       * opening a multi-hub policy showed one hub and saving it silently
       * dropped the rest. `inventory_places` is what the API returns for the
       * whole set; the legacy field is the fallback for rows written before.
       */
      inventory_ids:
        Array.isArray(row.inventory_places) && row.inventory_places.length
          ? row.inventory_places.map((h) => Number(h.id))
          : Array.isArray(row.inventory_list) && row.inventory_list.length
            ? row.inventory_list.map(Number)
            : [Number(row.inventory)].filter(Boolean),
      inventory: row.inventory,
      is_active: row.is_active !== false,
      mpin_status: row.mpin_status || 'DISABLED',
      inactivity_threshold_days:
        row.inactivity_threshold_days == null
          ? ''
          : String(row.inactivity_threshold_days),
      otp_channels: decodeOtpChannels(row.otp_channel),
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    // Editing and creating now take the same shape — a list. Editing used to
    // send one hub, which is how a multi-hub policy lost its other hubs.
    const hubIds = (form.inventory_ids || []).map(Number).filter(Boolean);

    if (!hubIds.length) {
      showToast('warn', 'Validation', 'Select at least one hub');
      return;
    }
    if (!form.otp_channels?.length) {
      showToast('warn', 'Validation', 'Select at least one OTP channel');
      return;
    }
    // Blank means unlimited — the MPIN never expires on its own.
    const rawDays = String(form.inactivity_threshold_days ?? '').trim();
    const days = rawDays === '' ? null : Number(rawDays);
    if (days !== null && (!Number.isFinite(days) || days < 1 || !Number.isInteger(days))) {
      showToast(
        'warn',
        'Validation',
        'Enter a whole number of days, or leave blank for unlimited'
      );
      return;
    }

    const otp_channel = encodeOtpChannels(form.otp_channels);
    const shared = {
      mpin_status: form.mpin_status,
      inactivity_threshold_days: days,
      otp_channel,
      is_active: form.is_active !== false,
    };

    try {
      setSaving(true);
      if (editMode && selectedId) {
        await CustomAxios.patch(
          `mpin-config/${selectedId}/`,
          // The list is the source of truth. `inventory` is cleared so the old
          // single-hub field cannot keep pointing at a hub the admin removed.
          { inventory_list: hubIds, inventory: null, ...shared },
          { timeout: REQ_TIMEOUT_MS }
        );
        showToast('success', 'Saved', 'MPIN config updated');
      } else {
        const res = await CustomAxios.post(
          'mpin-config/bulk/',
          { inventories: hubIds, ...shared },
          { timeout: REQ_TIMEOUT_MS }
        );
        const created = res.data?.created_count ?? 0;
        const skipped = res.data?.skipped_count ?? 0;
        if (created === 0 && skipped > 0) {
          showToast(
            'warn',
            'Nothing created',
            'Selected hubs already have MPIN config (existing rows left unchanged).'
          );
        } else {
          showToast(
            'success',
            'Created',
            skipped
              ? `Created ${created} hub(s); skipped ${skipped} existing.`
              : `Created MPIN config for ${created} hub(s).`
          );
        }
      }
      setModalOpen(false);
      setForm(emptyForm);
      // Silent refresh — avoid full-page loader sticking after save
      await fetchConfigs({ showLoader: false });
    } catch (err) {
      const detail =
        err?.code === 'ECONNABORTED'
          ? 'Request timed out — try fewer hubs or check the API'
          : err?.response?.data?.inventories?.[0] ||
            err?.response?.data?.inventory?.[0] ||
            err?.response?.data?.inactivity_threshold_days?.[0] ||
            err?.response?.data?.mpin_status?.[0] ||
            err?.response?.data?.otp_channel?.[0] ||
            err?.response?.data?.non_field_errors?.[0] ||
            err?.response?.data?.detail ||
            (typeof err?.response?.data === 'string'
              ? err.response.data
              : null) ||
            JSON.stringify(err?.response?.data || {}) ||
            'Save failed';
      showToast('error', 'Error', String(detail));
    } finally {
      setSaving(false);
    }
  };

  const configuredHubIds = useMemo(
    () => new Set(configs.map((c) => Number(c.inventory))),
    [configs]
  );

  const availableHubOptions = useMemo(
    () =>
      editMode
        ? hubOptions
        : hubOptions.filter((h) => !configuredHubIds.has(Number(h.value))),
    [configuredHubIds, editMode, hubOptions]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Toast ref={toast} />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6">MPIN Config (per hub)</Typography>
          <Typography variant="body2" color="text.secondary">
            Controls mobile quick-unlock: Mandatory / Optional / Disabled,
            inactivity OTP days, and OTP channel(s). Hubs without a row default
            to Disabled. Existing rows are never overwritten on bulk create.
          </Typography>
        </Box>
        <GrowthPrimaryButton startIcon={<AddIcon />} onClick={openCreate}>
          Add hub config
        </GrowthPrimaryButton>
      </Box>

      {loading ? (
        <GrowthPageLoader label="Loading MPIN configs…" />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Hub</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Inactivity (days)</TableCell>
                <TableCell>OTP channel</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    No configs yet — mobile defaults to MPIN disabled for hubs
                    without a row.
                  </TableCell>
                </TableRow>
              )}
              {configs.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.inventory_place || hubLabel(row.inventory)}
                  </TableCell>
                  <TableCell>{row.mpin_status}</TableCell>
                  <TableCell>
                    {row.inactivity_threshold_days == null
                      ? 'Unlimited'
                      : row.inactivity_threshold_days}
                  </TableCell>
                  <TableCell>{formatOtpChannelLabel(row.otp_channel)}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => openEdit(row)} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => setDeleteTarget(row)}
                      size="small"
                      aria-label="Delete MPIN config"
                    >
                      <DeleteOutlineIcon fontSize="small" color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <GrowthModal
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete MPIN config"
        subtitle={
          deleteTarget
            ? `${deleteTarget.inventory_place || hubLabel(deleteTarget.inventory)} will stop enforcing MPIN. Customers keep their existing PIN.`
            : ''
        }
        actions={
          <>
            <GrowthSecondaryButton
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleDelete} loading={deleting}>
              Delete
            </GrowthPrimaryButton>
          </>
        }
      />

      <GrowthModal
        open={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editMode ? 'Edit MPIN config' : 'Add MPIN config'}
        subtitle={
          editMode
            ? 'Update quick-unlock policy for this hub'
            : 'Apply the same policy to one or many hubs'
        }
        maxWidth={{ xs: '94%', sm: 520 }}
        busy={saving}
        busyLabel={editMode ? 'Updating…' : 'Creating…'}
        actions={
          <>
            <GrowthSecondaryButton
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </GrowthSecondaryButton>
            <GrowthPrimaryButton onClick={handleSave} loading={saving}>
              Save
            </GrowthPrimaryButton>
          </>
        }
      >
        <GrowthFormGrid>
          <Grid item xs={12}>
            {/*
              One picker for both modes.

              Editing used to show a single, DISABLED hub select — so a policy
              covering several hubs could neither be seen in full nor changed,
              and the only way to move a hub between policies was to delete and
              recreate. Editing now offers every hub, because the hubs this
              policy already holds must remain selectable; creating still hides
              hubs that belong to another active policy.
            */}
            {(
              <Autocomplete
                multiple
                options={editMode ? hubOptions : availableHubOptions}
                getOptionLabel={(option) => option.name}
                value={(editMode ? hubOptions : availableHubOptions).filter((h) =>
                  form.inventory_ids.includes(Number(h.value))
                )}
                onChange={(_, newValue) =>
                  setForm({
                    ...form,
                    inventory_ids: newValue.map((h) => Number(h.value)),
                  })
                }
                disableCloseOnSelect
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Hubs"
                    placeholder="Select one or more hubs"
                    helperText={
                      editMode
                        ? 'One policy can cover several hubs. Removing a hub here switches MPIN off for it.'
                        : 'Multi-select. Hubs already covered by an active policy are hidden.'
                    }
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.value}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    {option.name}
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      key={option.value}
                      label={option.name}
                      size="small"
                      {...getTagProps({ index })}
                    />
                  ))
                }
                isOptionEqualToValue={(option, value) =>
                  Number(option.value) === Number(value.value)
                }
                limitTags={4}
              />
            )}
          </Grid>

          {/*
            Switch a policy off without deleting it. An inactive policy is
            ignored everywhere, so its hubs stop asking for an MPIN — and the
            settings are still there when it is switched back on.
          */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active !== false}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
              }
              label={
                form.is_active !== false
                  ? 'Active — these hubs use this policy'
                  : 'Inactive — these hubs will not ask for an MPIN'
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>MPIN status</InputLabel>
              <Select
                label="MPIN status"
                value={form.mpin_status}
                onChange={(e) =>
                  setForm({ ...form, mpin_status: e.target.value })
                }
              >
                <MenuItem value="MANDATORY">Mandatory</MenuItem>
                <MenuItem value="OPTIONAL">Optional</MenuItem>
                <MenuItem value="DISABLED">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Inactivity threshold (days)"
              placeholder="Leave blank for unlimited"
              helperText="Blank = unlimited; the MPIN never expires on its own."
              value={form.inactivity_threshold_days}
              onChange={(e) =>
                setForm({
                  ...form,
                  inactivity_threshold_days: e.target.value,
                })
              }
              inputProps={{ min: 1, step: 1 }}
              helperText="After this many days without unlock, OTP is required before MPIN"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={OTP_OPTIONS}
              getOptionLabel={(option) => option.label}
              value={OTP_OPTIONS.filter((o) =>
                form.otp_channels.includes(o.value)
              )}
              onChange={(_, newValue) =>
                setForm({
                  ...form,
                  otp_channels: newValue.map((o) => o.value),
                })
              }
              disableCloseOnSelect
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="OTP channels"
                  placeholder="SMS and/or WhatsApp"
                  helperText="Select one or both. WhatsApp falls back to SMS if unavailable."
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.value}>
                  <Checkbox checked={selected} style={{ marginRight: 8 }} />
                  {option.label}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
            />
          </Grid>
        </GrowthFormGrid>
      </GrowthModal>
    </Box>
  );
}

export default MpinConfig;
